import axiosInterceptor from 'services/axiosInterceptor'
import { API_BASE_URL } from 'services/constants'

import type {
	ExplorerColumn,
	ExplorerGrain,
	ExplorerGrainKey,
	ExplorerInterpretInput,
	ExplorerInterpretResult,
	ExplorerPreviewInput,
	ExplorerReport,
	ExplorerReportScope,
	ExplorerRowsResponse,
	ExplorerScopeAsset,
	ExplorerScopeType,
	ExplorerTemplateDraft,
	ExplorerTemplateScope,
	SaveAsTemplateInput,
	SaveExplorerReportInput,
} from '../types/explorer.types'

/**
 * HTTP layer for `/api/explorer` (MYD-359).
 *
 * Pure axios: no Redux, no UI concerns. `slices/explorerSlice.ts` wraps these
 * in thunks so components only ever see actions and state, matching the
 * reportService / reportSlice split.
 *
 * Imports `services/axiosInterceptor` DIRECTLY rather than through the
 * `services` barrel. The barrel pulls the whole store graph and re-creates the
 * DT-029 cycle (interceptor to store to slices to a service to the interceptor
 * again), which resolved to undefined and threw "axiosInterceptor is not a
 * function" at import time. `slices/noCircularImport.test.ts` is the net for
 * that, and importing the module directly stays on the right side of it.
 *
 * The interceptor attaches the session token per request, so nothing here takes
 * a token argument.
 */
const api = () => axiosInterceptor(API_BASE_URL)

/** Row types this user can build reports on, including the not-yet-available ones. */
export async function getGrains(): Promise<ExplorerGrain[]> {
	const { data } = await api().get<ExplorerGrain[]>('/explorer/grains')
	return data
}

/**
 * The column catalog for a grain, already filtered to this user's capabilities.
 * The picker renders whatever comes back; it never holds its own column list.
 */
export async function getColumns(grain: ExplorerGrainKey): Promise<ExplorerColumn[]> {
	const { data } = await api().get<ExplorerColumn[]>('/explorer/columns', { params: { grain } })
	return data
}

/**
 * Run a definition and return a page of rows.
 *
 * A POST rather than a GET so the whole working definition, including filters
 * and sorts, travels in the body instead of being encoded into fragile query
 * params. Unsaved grids and saved reports being edited share this one path.
 */
export async function previewRows(input: ExplorerPreviewInput): Promise<ExplorerRowsResponse> {
	const { data } = await api().post<ExplorerRowsResponse>('/explorer/preview', input)
	return data
}

export async function listReports(grain?: ExplorerGrainKey): Promise<ExplorerReport[]> {
	const { data } = await api().get<ExplorerReport[]>('/explorer/reports', {
		params: grain ? { grain } : undefined,
	})
	return data
}

export async function getReport(id: string): Promise<ExplorerReport> {
	const { data } = await api().get<ExplorerReport>(`/explorer/reports/${id}`)
	return data
}

export async function createReport(input: SaveExplorerReportInput): Promise<ExplorerReport> {
	const { data } = await api().post<ExplorerReport>('/explorer/reports', input)
	return data
}

export async function updateReport(
	id: string,
	input: Partial<SaveExplorerReportInput>,
): Promise<ExplorerReport> {
	const { data } = await api().put<ExplorerReport>(`/explorer/reports/${id}`, input)
	return data
}

export async function deleteReport(id: string): Promise<void> {
	await api().delete(`/explorer/reports/${id}`)
}

/**
 * Run a SAVED report.
 *
 * Only paging is overridable here; anything structural goes through
 * `previewRows` instead. Rows are always re-queried as the caller, so a shared
 * report shows each viewer only what they may see.
 */
export async function getReportRows(
	id: string,
	params: { page?: number; perPage?: number; includeArchived?: boolean } = {},
): Promise<ExplorerRowsResponse> {
	const { data } = await api().get<ExplorerRowsResponse>(`/explorer/reports/${id}/rows`, { params })
	return data
}

/**
 * Export a report as CSV.
 *
 * Fetched as a blob over XHR rather than by navigating to the endpoint. CRA's
 * dev proxy ignores requests carrying a `text/html` Accept header, so a
 * `window.open` or a plain link 404s in development while working in
 * production — the trap documented in `services/reports/reportService.ts`
 * (MYD-344).
 *
 * An error response arrives as a Blob too, since `responseType` applies to
 * every status. It is read back as text so the server's message (which names
 * the offending column) survives instead of surfacing as "[object Blob]".
 */
export type ExplorerExportFormat = (typeof EXPORT_FORMATS)[number]

/** The formats the server accepts. Kept in step with EXPORT_FORMATS there. */
export const EXPORT_FORMATS = ['csv', 'xlsx', 'pdf'] as const

export async function exportReport(
	body: ExplorerPreviewInput & { name?: string; reportId?: string; format?: ExplorerExportFormat },
): Promise<{ blob: Blob; filename: string }> {
	try {
		const response = await api().post('/explorer/export', body, { responseType: 'blob' })

		// The server names the file; parsing it back keeps one source of truth
		// for the naming rules rather than duplicating them on the client.
		const disposition = String(response.headers?.['content-disposition'] ?? '')
		const match = disposition.match(/filename="?([^"]+)"?/)

		return {
			blob: response.data as Blob,
			filename: match?.[1] ?? `explorer-report.${body.format ?? 'csv'}`,
		}
	} catch (error: any) {
		const blob = error?.response?.data
		if (blob instanceof Blob) {
			try {
				const parsed = JSON.parse(await blob.text())
				error.explorerMessage = parsed?.message
			} catch {
				// Not JSON; leave the generic message in place.
			}
		}
		throw error
	}
}

/**
 * The values a filterable column actually holds, for its filter picker.
 *
 * Offering real values beats a free-text box: a user filtering by status should
 * not have to remember it is stored as "S1 - Open" rather than "Open". The list
 * is scoped to rows the caller can already see, and capped server-side.
 */
export async function getFacetValues(
	grain: ExplorerGrainKey,
	column: string,
	scope?: ExplorerReportScope | null,
): Promise<string[]> {
	// A scoped report's picker offers only the values its own rows hold
	// (MYD-596). A GET, so the scope travels as two params.
	const params: Record<string, string> = { grain, column }
	if (scope?.id) {
		params.scopeType = scope.type
		params.scopeId = scope.id
	}
	const { data } = await api().get<string[]>('/explorer/facets', { params })
	return data
}

// =============================================================================
// Templates (MYD-597 endpoints, MYD-598 UI)
// =============================================================================

/** My templates and partners' shared ones, optionally for one gallery tab. */
export async function listTemplates(templateScope?: ExplorerTemplateScope): Promise<ExplorerReport[]> {
	const { data } = await api().get<ExplorerReport[]>('/explorer/templates', {
		params: templateScope ? { scope: templateScope } : undefined,
	})
	return data
}

/** File a report I can read as one of my templates. */
export async function saveReportAsTemplate(reportId: string, input: SaveAsTemplateInput): Promise<ExplorerReport> {
	const { data } = await api().post<ExplorerReport>(`/explorer/reports/${reportId}/save-as-template`, input)
	return data
}

/**
 * An unsaved report from a template, bound to one asset. Not persisted: the
 * user opens it as a draft and saves it only if they want to keep it.
 */
export async function instantiateTemplateDraft(
	templateId: string,
	scope: ExplorerReportScope | null,
): Promise<ExplorerTemplateDraft> {
	const { data } = await api().post<ExplorerTemplateDraft>(`/explorer/templates/${templateId}/instantiate`, {
		scope,
		persist: false,
	})
	return data
}

/** Assets of one type a report can be scoped to, by name, for a picker. */
export async function listScopeAssets(type: ExplorerScopeType, q?: string): Promise<ExplorerScopeAsset[]> {
	const { data } = await api().get<ExplorerScopeAsset[]>(`/explorer/scopes/${type}`, {
		params: q ? { q } : undefined,
	})
	return data
}

/** One scoped asset's name, for the scope chip. Rejects with a 403 if not openable. */
export async function getScopeAsset(type: ExplorerScopeType, id: string): Promise<ExplorerScopeAsset> {
	const { data } = await api().get<ExplorerScopeAsset>(`/explorer/scopes/${type}/${id}`)
	return data
}

/**
 * Ask for the filters in plain words (MYD-628): "open tasks due this month
 * assigned to Mary, grouped by project". Lives under /api/ai, behind AI
 * Assist's guards and spending limits. Answers only; nothing is saved.
 */
export async function interpretFilters(input: ExplorerInterpretInput): Promise<ExplorerInterpretResult> {
	const { data } = await api().post<ExplorerInterpretResult>('/ai/explorer/interpret', input)
	return data
}
